import { buildCanvas2dPipeline } from "../pipelines/canvas2d/canvas2dPipeline";
import { buildWebGL2Pipeline } from "../pipelines/webgl2/webgl2Pipeline";
import { createTimerWorker } from "../helpers/timerHelper";
import { BackgroundConfig } from "../helpers/backgroundHelper";
import { RenderingPipeline } from "../helpers/renderingPipelineHelper";
import { SegmentationConfig } from "../helpers/segmentationHelper";
import { SourcePlayback } from "../helpers/sourceHelper";
import { TFLite } from "./useTFLite";
import { InferenceWorker } from "./useInferenceWorker";
import type { SegmenterHandle } from "./useImageSegmenter";
import { ref } from "vue";
import { concatUrlParts } from "@/utils";
import axios from "axios";

function useRenderingPipeline(
  sourcePlayback: SourcePlayback,
  backgroundConfig: BackgroundConfig,
  segmentationConfig: SegmentationConfig,
  tflite: TFLite,
  inferenceWorker: InferenceWorker | null = null,
  imageSegmenter: SegmenterHandle | null = null,
) {
  let pipeline: RenderingPipeline = null;
  let backgroundImageRef: HTMLImageElement = null;
  let canvasRef: HTMLCanvasElement = null;

  const backgroundImage = new Image();
  backgroundImage.crossOrigin = "anonymous";

  // prepend the base url if the url is relative. urlencoded is needed for spaces in the url
  if (!backgroundConfig.url.startsWith("http")) {
    //@ts-ignore
    backgroundImage.src = concatUrlParts(axios.defaults.baseURL, "/api/background/", encodeURI(backgroundConfig.url));
  } else {
    backgroundImage.src = backgroundConfig.url;
  }

  backgroundImageRef = backgroundImage;
  // Not appended to the DOM: an <img> works as a WebGL texture source once loaded without being
  // attached, and appending a fresh one on every pipeline rebuild leaked elements onto <body>.

  // create canvas for rendering
  const canvas =
    window["backgroundCanvasEvement" + segmentationConfig.pipeline + segmentationConfig.model] ||
    (() => {
      let cvs = document.createElement("canvas");
      cvs.width = sourcePlayback.width;
      cvs.height = sourcePlayback.height;
      document.body.appendChild(cvs);
      // @ts-ignore
      window["backgroundCanvasEvement" + segmentationConfig.pipeline + segmentationConfig.model] = cvs;
      return cvs;
    })();

  canvasRef = canvas;

  const fps = ref(0);
  const durations = ref<number[]>([]);

  const targetTimerTimeoutMs = 1000 / segmentationConfig.targetFps;

  let previousTime = 0;
  let beginTime = 0;
  let eventCount = 0;
  let frameCount = 0;
  const frameDurations: number[] = [];

  let renderTimeoutId: number;

  const timerWorker = createTimerWorker();

  const newPipeline =
    segmentationConfig.pipeline === "webgl2"
      ? buildWebGL2Pipeline(
          sourcePlayback,
          backgroundImageRef,
          backgroundConfig,
          segmentationConfig,
          canvasRef,
          tflite,
          timerWorker,
          addFrameEvent,
          inferenceWorker,
          imageSegmenter,
        )
      : buildCanvas2dPipeline(
          sourcePlayback,
          backgroundImageRef,
          backgroundConfig,
          segmentationConfig,
          canvasRef,
          tflite,
          addFrameEvent,
        );

  async function render() {
    const startTime = performance.now();

    beginFrame();
    if (pipeline && pipeline.cleanedUp) {
      return;
    }
    await newPipeline.render();
    endFrame();

    renderTimeoutId = timerWorker.setTimeout(render, Math.max(0, targetTimerTimeoutMs - (performance.now() - startTime)));
  }

  function beginFrame() {
    beginTime = Date.now();
  }

  function addFrameEvent() {
    const time = Date.now();
    frameDurations[eventCount] = time - beginTime;
    beginTime = time;
    eventCount++;
  }

  function endFrame() {
    const time = Date.now();
    frameDurations[eventCount] = time - beginTime;
    frameCount++;
    if (time >= previousTime + 1000) {
      fps.value = (frameCount * 1000) / (time - previousTime);
      durations.value = frameDurations;
      previousTime = time;
      frameCount = 0;
    }
    eventCount = 0;
  }

  render();
  console.log("Animation started:", sourcePlayback, backgroundConfig, segmentationConfig);

  pipeline = newPipeline;

  return {
    pipeline,
    backgroundImageRef,
    canvasRef,
    fps,
    durations,
    timerWorker,
  };
}

export default useRenderingPipeline;
